import React from 'react';

/**
 * Pagination component props
 */
export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (limit: number) => void;
  perPageOptions?: number[];
}

/**
 * Pagination component - displays page navigation controls
 *
 * Features:
 * - Page number buttons with ellipsis for large ranges
 * - Items per page selector
 * - Previous/Next navigation
 * - Results count display
 * - Fully keyboard accessible
 *
 * @example
 * <Pagination
 *   currentPage={2}
 *   totalPages={20}
 *   totalItems={487}
 *   itemsPerPage={25}
 *   onPageChange={(page) => setPage(page)}
 *   onItemsPerPageChange={(limit) => setLimit(limit)}
 * />
 */
export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  perPageOptions = [10, 25, 50, 100],
}) => {
  // Calculate which page numbers to show
  const pageNumbers = calculatePageNumbers(currentPage, totalPages);

  // Calculate item range for current page
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  if (totalItems === 0) {
    return (
      <div className="flex items-center justify-center py-4 text-sm text-gray-500 dark:text-gray-400">
        No items to display
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 py-4 border-t border-gray-200 dark:border-gray-700">
      {/* Top row: Results count */}
      <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
        <div>
          Showing <span className="font-medium text-gray-900 dark:text-white">{startItem}</span> to{' '}
          <span className="font-medium text-gray-900 dark:text-white">{endItem}</span> of{' '}
          <span className="font-medium text-gray-900 dark:text-white">{totalItems}</span> items
        </div>

        {/* Items per page selector */}
        <div className="flex items-center gap-2">
          <span>Per page:</span>
          <select
            value={itemsPerPage}
            onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
            className="input py-1 px-2 text-sm w-20"
            aria-label="Items per page"
          >
            {perPageOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Bottom row: Page navigation */}
      <div className="flex items-center justify-center gap-1">
        {/* Previous button */}
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="btn btn-secondary text-sm px-3 py-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Previous page"
        >
          ← Prev
        </button>

        {/* Page number buttons */}
        {pageNumbers.map((pageNum, index) => {
          if (pageNum === 'ellipsis') {
            return (
              <span key={`ellipsis-${index}`} className="px-2 text-gray-500">
                ...
              </span>
            );
          }

          const isActive = pageNum === currentPage;

          return (
            <button
              key={pageNum}
              onClick={() => onPageChange(pageNum)}
              className={`
                px-3 py-1.5 text-sm rounded transition-colors
                ${
                  isActive
                    ? 'bg-primary-800 text-white font-medium'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }
                border border-gray-300 dark:border-gray-600
              `}
              aria-label={`Page ${pageNum}`}
              aria-current={isActive ? 'page' : undefined}
            >
              {pageNum}
            </button>
          );
        })}

        {/* Next button */}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="btn btn-secondary text-sm px-3 py-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Next page"
        >
          Next →
        </button>
      </div>
    </div>
  );
};

/**
 * Calculates which page numbers to display with ellipsis
 * Algorithm: [1] ... [5] [6] [7] ... [20]
 */
function calculatePageNumbers(current: number, total: number): (number | 'ellipsis')[] {
  const MAX_VISIBLE = 7; // Maximum page buttons to show

  // Show all if total fits
  if (total <= MAX_VISIBLE) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const result: (number | 'ellipsis')[] = [];
  const showLeftEllipsis = current > 3;
  const showRightEllipsis = current < total - 2;

  // Always show first page
  result.push(1);

  // Left ellipsis
  if (showLeftEllipsis) {
    result.push('ellipsis');
  }

  // Middle pages around current
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);

  for (let i = start; i <= end; i++) {
    result.push(i);
  }

  // Right ellipsis
  if (showRightEllipsis) {
    result.push('ellipsis');
  }

  // Always show last page
  if (total > 1) {
    result.push(total);
  }

  return result;
}
